import { Route, Switch, Redirect, BrowserRouter } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";

import TunnelContext from "./TunnelContext";
import ConfigProvider from "./ConfigProvider";
import GoogleTagManager from "./components/tracking/GoogleTagManager";
import PrivacyDidomi from "./components/tracking/PrivacyDidomi";
import FacebookPixel from "./components/tracking/FacebookPixel";
import TikTokPixel from "./components/tracking/TikTokPixel";

const ClubSelection = lazy(() => import("./steps/ClubSelection"));
const UserForm = lazy(() => import("./steps/UserForm"));
const Plans = lazy(() => import("./steps/Plans"));
const Sepa = lazy(() => import("./steps/Sepa"));
const ContractWithoutSMS = lazy(() => import("./steps/ContractWithoutSMS"));
const Payment = lazy(() => import("./steps/Payment"));
const Done = lazy(() => import("./steps/Done"));
const StepConfirmation = lazy(() =>
  import("./steps/StepConfirmation").then((m) => ({
    default: m.StepConfirmation,
  })),
);

function getLocaleConfig(hostname) {
  if (hostname.endsWith("es") || hostname.endsWith("es.localhost"))
    return { configCode: "onair.es", language: "es", title: "Me suscribo" };
  if (hostname.endsWith("pt") || hostname.endsWith("pt.localhost"))
    return { configCode: "onair.pt", language: "pt", title: "Subscrever" };
  if (hostname.endsWith("ma") || hostname.endsWith("ma.localhost"))
    return { configCode: "onair.ma", language: "fr", title: "Je m'abonne" };
  if (
    hostname.endsWith("preprod.masalledesport.com") ||
    hostname.endsWith("test") ||
    hostname.endsWith(".localhost")
  )
    return {
      configCode: "onair.integ",
      language: "fr",
      title: "ON AIR INTEGRATION",
    };
  return { configCode: "onair.fr", language: "fr", title: "Je m'abonne" };
}

function App() {
  const { configCode, language, title } = getLocaleConfig(
    window.location.hostname,
  );

  useEffect(() => {
    window.i18n.changeLanguage(language);
    document.title = title;
    document.documentElement.lang = language;
  }, []);

  const [isDidomiReady, setDidomiReady] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTrackingEnabled(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDidomiReady = () => {
    setDidomiReady(true);
  };

  return (
    <>
      {trackingEnabled && (
        <>
          <PrivacyDidomi
            configCode={configCode}
            onDidomiReady={handleDidomiReady}
          />
          <GoogleTagManager
            configCode={configCode}
            isDidomiReady={isDidomiReady}
          />
          <FacebookPixel />
          <TikTokPixel />
        </>
      )}
      <ConfigProvider configCode={configCode}>
        <ConfiguredApp />
      </ConfigProvider>
    </>
  );
}

export default App;

function ConfiguredApp() {
  return (
    <BrowserRouter>
      <TunnelContext>
        <Suspense fallback={null}>
          <Switch>
            <Route exact path={`/`} component={ClubSelection} />
            <Route exact path={`/user`} component={UserForm} />
            <Route exact path={`/plans`} component={Plans} />
            <Route exact path={`/sepa`} component={Sepa} />
            <Route exact path={`/contract`} component={ContractWithoutSMS} />
            <Route exact path={`/card`} component={Payment} />
            <Route
              exact
              path={`/middle-confirmation`}
              component={StepConfirmation}
            />
            <Route exact path={`/confirmation`} component={Done} />
            <Redirect from={"*"} to={`/`} />
          </Switch>
        </Suspense>
      </TunnelContext>
    </BrowserRouter>
  );
}
